import React, { useEffect, useState } from "react";
import { Button, IconSite, useFormattingLanguage } from "@siteimprove/fancylib";
import { BigSmall } from "../../text/big-small/big-small";
import {
	FormattedNumber,
	toFormattedNumberString,
} from "../../text/formatted-number/formatted-number";
import { Starred } from "../starred/starred";
import { PageHeaderPicker } from "../../structure/page-header-picker/page-header-picker";
import { SortField } from "../../tables-and-lists/column-header/column-header";
import { Table } from "../../tables-and-lists/table/table";
import {
	BaseTableCellPosition,
	ColumnConfig,
	ColumnsArray,
} from "../../tables-and-lists/base-table/base-table";
import { useLabTranslations } from "../../../translations/translations";
import scss from "./site-picker.scss";

export type Site = {
	id: number;
	name: string;
	url: string;
	isFavorite: boolean;
	imageSrc: string;
};

interface SitePickerProps {
	/** Items displayed in the site picker. */
	items: Site[];
	/** Total amount of sites. */
	totalItems: number;
	/** Property and direction by which the sites table is sorted. */
	sort: SortField<Site>;
	/** Callback for updating sorting. */
	setSort: (property: keyof Site, direction: "asc" | "desc") => void;
	/** Search options to filter sites. */
	search: {
		/** Search query. */
		query: string;
		/** Search callback to search for site names or URLs. */
		onSearch: (query: string) => void;
	};
	/** Load more items callback */
	onLoadMore: () => void;
	/** Callback to favorite/unfavorite a site */
	onFavorite: (site: Site, isFavorite: boolean) => void;
	/** Callback when a site is selected */
	onSelectedSite: (site: Site) => void;
	/** Selected site item */
	selectedSite?: Site;
	/** If the site picker is loading */
	loading?: boolean;
	/** Extra columns to be displayed in the site picker additional to the default columns */
	extraColumns?: ColumnsArray<Site>;
	/** URL to edit sites page */
	editSitesUrl?: string;
	/** Texts for the site picker */
	texts?: {
		selectASite?: string;
		editSites?: string;
		favoriteAriaLabel?: string;
		showingXOfYItems?: (showing: number, total: number) => string;
		loadMore?: (count: number) => string;
	};
}

export function SitePicker(props: SitePickerProps): JSX.Element {
	const {
		items,
		totalItems,
		search,
		sort,
		setSort,
		editSitesUrl,
		onLoadMore,
		onFavorite,
		onSelectedSite,
		loading,
		extraColumns,
	} = props;

	const i18n = useLabTranslations();
	const locale = useFormattingLanguage();

	const [selectedSite, setSelectedSite] = useState(props.selectedSite);

	const texts = {
		selectASite: (props.texts && props.texts.selectASite) || i18n.selectASite,
		editSites: (props.texts && props.texts.editSites) || i18n.editSites,
		favoriteAriaLabel: (props.texts && props.texts.favoriteAriaLabel) || i18n.favorite,
		showingXOfYItems: (showing: number, total: number) =>
			props.texts && props.texts.showingXOfYItems
				? props.texts.showingXOfYItems(showing, total)
				: i18n.showingXOfYSites
						.replace(
							"[COUNT]",
							toFormattedNumberString({ format: "number", number: showing, locale })
						)
						.replace(
							"[TOTAL]",
							toFormattedNumberString({ format: "number", number: total, locale })
						),
		loadMore: (count: number) =>
			props.texts && props.texts.loadMore
				? props.texts.loadMore(count)
				: i18n.loadXMore.replace(
						"[COUNT]",
						toFormattedNumberString({ format: "number", number: count, locale })
				  ),
	};

	const { scrollIntoViewRef, take } = useScrollIntoView(items.length, loading);

	return (
		<PageHeaderPicker
			itemsCount={items.length}
			totalItems={totalItems}
			selectedItem={selectedSite}
			selectedItemStringify={(site) => site.name}
			search={search}
			take={take}
			onLoadMore={onLoadMore}
			loading={loading}
			popoverButtonIcon={<IconSite />}
			texts={{ ...texts, buttonContentNoItemSelected: texts.selectASite }}
			toolbarActions={
				editSitesUrl && (
					<Button variant="primary" href={editSitesUrl}>
						{texts.editSites}
					</Button>
				)
			}
			contentItems={(firstFocusableRef, close, tableClassName, selectButtonClassName) => (
				<Table
					items={items}
					columns={[
						{
							header: { content: "Favorite", property: "isFavorite" },
							render: (item) => (
								<Starred
									isStarred={item.isFavorite}
									onChange={(starred) => onFavorite(item, starred)}
									aria-label={texts.favoriteAriaLabel}
								/>
							),
						},
						{
							header: [
								{ content: "Title", property: "name" },
								{ content: "URL", property: "url" },
							],
							render: (item, cellPosition) => (
								<Button
									variant="borderless"
									className={selectButtonClassName}
									onClick={() => {
										onSelectedSite(item);
										setSelectedSite(item);
										close();
									}}
									ref={
										cellPosition.rowNum === 0 ? firstFocusableRef : scrollIntoViewRef(cellPosition)
									}
								>
									<img role="presentation" src={item.imageSrc} className={scss.imgSite} />
									<BigSmall
										className={scss.titleUrl}
										big={<span className={scss.ellipsis}>{item.name}</span>}
										small={<span className={scss.ellipsis}>{item.url}</span>}
									/>
								</Button>
							),
							options: { isKeyColumn: true, cellPadding: "none" },
						},
						...(extraColumns ? extraColumns : []),
					]}
					loading={false}
					sort={sort}
					setSort={setSort}
					highlightRow={(item) => item.name === selectedSite?.name}
					className={tableClassName}
				/>
			)}
		/>
	);
}

function useScrollIntoView(
	itemsCount: number,
	loading?: boolean
): {
	scrollIntoViewRef: (
		cellPosition: BaseTableCellPosition
	) => React.Ref<HTMLButtonElement> | undefined;
	take: number;
} {
	const scrollIntoViewRef = React.createRef<HTMLButtonElement>();
	const itemsCountRef = React.useRef(itemsCount);

	useEffect(() => {
		if (!loading && itemsCountRef.current !== itemsCount) {
			itemsCountRef.current = itemsCount;
			scrollIntoViewRef.current?.scrollIntoView({ behavior: "smooth" });
		}
	}, [itemsCount, loading]);

	const take = 20;

	return {
		scrollIntoViewRef: (cellPosition: BaseTableCellPosition) =>
			itemsCount > take && cellPosition.rowNum === itemsCount - take
				? scrollIntoViewRef
				: undefined,
		take,
	};
}

export function colPages(): ColumnConfig<Site & { pages?: number | null }> {
	return {
		header: { content: "Pages", property: "pages" },
		render: (item) => <FormattedNumber number={item.pages || null} format="number" />,
		options: { align: "right" },
	};
}

export function colVisits(): ColumnConfig<Site & { visits?: number | null }> {
	return {
		header: { content: "Visits", property: "visits" },
		render: (item) => <FormattedNumber number={item.visits || null} format="number" />,
		options: { align: "right" },
	};
}
